// Chapter 1: Insurance Fundamentals for Crop
// Sections 3-4

export const ch1Section3 = {
  title: "Indemnity and Insurable Interest",
  content: `
    <h2>Indemnity and Insurable Interest</h2>
    <h3>The Principle of Indemnity</h3>
    <p><strong>Indemnity</strong> is the foundational principle that insurance should restore the insured to the same financial position before the loss—no better and no worse. Insurance is NOT designed to create profit.</p>
    <div class="key-concept">
      <strong>The Indemnity Principle:</strong>
      <p>If a Brookings County farmer's actual yield is 120 bu/acre when their guarantee was 175 bu/acre, the indemnity covers the 55-bushel shortfall. They don't receive payment for corn actually harvested—only for the loss below guarantee.</p>
    </div>
    <h3>Insurable Interest</h3>
    <p><strong>Insurable interest</strong> means you must have a financial stake in what you're insuring. This prevents wagering, reduces moral hazard, and supports indemnity.</p>
    <table>
      <tr><th>Party</th><th>Insurable Interest</th></tr>
      <tr><td><strong>Farm Owner-Operator</strong></td><td>Full interest in crops on owned land</td></tr>
      <tr><td><strong>Cash Rent Tenant</strong></td><td>Full interest—pays fixed rent, keeps all production</td></tr>
      <tr><td><strong>Share Rent Tenant</strong></td><td>Interest equal to their share (e.g., 60%)</td></tr>
      <tr><td><strong>Landlord (Share Rent)</strong></td><td>Interest equal to their share (e.g., 40%)</td></tr>
      <tr><td><strong>Agricultural Lender</strong></td><td>Interest to extent of loan secured by crop</td></tr>
    </table>
    <div class="example">
      <h4>Share Rent Example:</h4>
      <p>A Day County landowner rents 640 acres under 65/35 share arrangement. The tenant receives 65% of crop; landlord receives 35%. Each can only insure their own share—tenant insures 65%, landlord insures 35%.</p>
    </div>
    <div class="exam-tip">
      <strong>Exam Tip:</strong> Questions about insurable interest often involve share rent. Each party can only insure their own share.
    </div>
    <div class="important">
      <strong>Assignment:</strong> Indemnity payments can be <strong>assigned</strong> to a lender. Assignment doesn't transfer the policy—only the right to receive payment. The farmer remains insured with all duties.
    </div>
  `
};

export const ch1Section4 = {
  title: "Loss Valuation Methods",
  content: `
    <h2>Loss Valuation Methods</h2>
    <h3>Actual Cash Value (ACV)</h3>
    <p><strong>ACV</strong> = Replacement Cost − Depreciation. Accounts for current, used condition.</p>
    <div class="example">
      <p>A grain bin costing $50,000 new five years ago is destroyed. New bin costs $60,000. With 30% depreciation: $60,000 × 70% = $42,000 ACV settlement.</p>
    </div>
    <p><em>Note: ACV is NOT the primary method for crop losses. Crop insurance uses yield-based and revenue-based calculations.</em></p>
    <h3>Other Valuation Methods</h3>
    <p><strong>Replacement Cost:</strong> Cost to replace without depreciation deduction.</p>
    <p><strong>Market Value:</strong> Price a willing buyer would pay willing seller. For crops, fluctuates daily.</p>
    <div class="key-concept">
      <strong>Crop Insurance Valuation:</strong>
      <p>Crop insurance uses <strong>established prices</strong> (set by RMA before planting), <strong>projected prices</strong>, and <strong>harvest prices</strong> combined with yield or revenue guarantees.</p>
    </div>
    <p><strong>Stated/Agreed Value:</strong> Amount agreed at policy inception, paid for total loss.</p>
    <p><strong>Salvage Value:</strong> Remaining value of damaged property. In crop insurance, harvestable production offsets indemnity.</p>
    <h3>Direct Loss vs. Indirect Loss</h3>
    <table>
      <tr><th>Direct Loss</th><th>Indirect Loss (Consequential)</th></tr>
      <tr><td>Immediate physical damage</td><td>Secondary losses from direct loss</td></tr>
      <tr><td>Hail destroys corn crop</td><td>Farmer loses expected income</td></tr>
      <tr><td>Flood damages stored grain</td><td>Farmer must buy replacement feed</td></tr>
    </table>
    <div class="exam-tip">
      <strong>Exam Tip:</strong> MPCI covers <strong>direct losses</strong> to growing crops from insured perils. It does NOT cover indirect losses like lost profits or additional expenses.
    </div>
  `
};
